/**
 * LiveChat 内嵌客服（www.livechat.com）
 * license: 19997217
 * - set_customer_name / set_session_variables 写入客户ID
 * - maximize 打开聊天窗（不跳转页面）
 */

export const LIVECHAT_LICENSE = 19997217;
export const LIVECHAT_SCRIPT_SRC = "https://cdn.livechatinc.com/tracking.js";

let pluginLoadPromise = null;
let visibilityHooked = false;
let openingChat = false;
let pendingHideTimers = [];

const LIVECHAT_POSITION_CSS = `
  /* 缩小态圆球：抬高 + 默认隐藏，避免挡底部导航；展开窗不受影响 */
  #chat-widget-minimized,
  iframe#chat-widget-minimized {
    bottom: calc(5.5rem + env(safe-area-inset-bottom, 0px)) !important;
    opacity: 0 !important;
    pointer-events: none !important;
    transform: scale(0.01) !important;
  }

  /* 展开态：上下留空，顶部关闭按钮可见，底部不挡导航 */
  #chat-widget-container {
    top: max(0.75rem, env(safe-area-inset-top, 0px)) !important;
    bottom: calc(5.25rem + env(safe-area-inset-bottom, 0px)) !important;
    height: auto !important;
    max-height: none !important;
  }

  #chat-widget-container iframe,
  #chat-widget-container > div {
    max-height: 100% !important;
  }
`;

function injectLiveChatPositionStyle() {
  if (typeof document === "undefined") {
    return;
  }
  let style = document.getElementById("livechat-above-tabbar");
  if (!style) {
    style = document.createElement("style");
    style.id = "livechat-above-tabbar";
    document.head.appendChild(style);
  }
  style.textContent = LIVECHAT_POSITION_CSS;
}

function clearPendingHides() {
  pendingHideTimers.forEach((id) => clearTimeout(id));
  pendingHideTimers = [];
}

function scheduleHideLauncher(delayMs) {
  const id = setTimeout(() => {
    // 正在打开聊天时绝不能 hide，否则会把刚 maximize 的窗口藏掉
    if (openingChat) {
      return;
    }
    try {
      if (window.LiveChatWidget && typeof window.LiveChatWidget.call === "function") {
        window.LiveChatWidget.call("hide");
      }
    } catch (error) {
      /* ignore */
    }
  }, delayMs);
  pendingHideTimers.push(id);
}

function hookLiveChatVisibility() {
  if (visibilityHooked || !window.LiveChatWidget || typeof window.LiveChatWidget.on !== "function") {
    return;
  }
  visibilityHooked = true;
  try {
    window.LiveChatWidget.on("visibility_changed", (data) => {
      const vis = data && data.visibility;
      // 用户主动收起/关闭后，再藏悬浮球；打开过程中不要 hide
      if (!openingChat && (vis === "minimized" || vis === "hidden")) {
        scheduleHideLauncher(0);
      }
      if (vis === "maximized") {
        openingChat = false;
      }
    });
  } catch (error) {
    /* ignore */
  }
}

function readUserInfo(vm) {
  const store = vm && vm.$store;
  const state = (store && store.state) || {};
  const getters = (store && store.getters) || {};
  return (
    getters.getUserInfo ||
    state.userInfo ||
    state.user ||
    (vm && vm.userInfo) ||
    null
  );
}

/** 对接用客户 ID：优先会员编号 member_code，其次内部 id */
export function resolveMemberId(vm) {
  const userInfo = readUserInfo(vm);
  const code =
    (userInfo && userInfo.member_code) ||
    localStorage.getItem("member_code") ||
    "";
  if (code != null && String(code).trim()) {
    return String(code).trim();
  }

  const fromInfo =
    userInfo &&
    (userInfo.id || userInfo.user_id || userInfo.uid || userInfo.member_id);
  if (fromInfo != null && String(fromInfo).trim()) {
    return String(fromInfo).trim();
  }

  return (
    localStorage.getItem("id") ||
    localStorage.getItem("user_id") ||
    localStorage.getItem("uid") ||
    ""
  );
}

/** 展示名：优先 member_code，便于后台识别客户ID */
export function resolveKefuDisplayName(vm, userId) {
  const userInfo = readUserInfo(vm);
  const code =
    (userInfo && userInfo.member_code) ||
    localStorage.getItem("member_code") ||
    "";
  if (code != null && String(code).trim()) {
    return String(code).trim();
  }
  const name = (userInfo && (userInfo.username || userInfo.name)) || "";
  if (name) {
    return String(name).trim();
  }
  return String(userId || "").trim();
}

function ensureLiveChatBootstrap() {
  if (typeof window === "undefined") {
    return null;
  }
  window.__lc = window.__lc || {};
  window.__lc.license = LIVECHAT_LICENSE;
  window.__lc.integration_name = "manual_onboarding";
  window.__lc.product_name = "livechat";

  if (window.LiveChatWidget && typeof window.LiveChatWidget.call === "function") {
    return window.LiveChatWidget;
  }

  const api = {
    _q: [],
    _h: null,
    _v: "2.0",
    on() {
      api._q.push(["on", [].slice.call(arguments)]);
    },
    once() {
      api._q.push(["once", [].slice.call(arguments)]);
    },
    off() {
      api._q.push(["off", [].slice.call(arguments)]);
    },
    get() {
      if (!api._h) {
        throw new Error("[LiveChatWidget] You can't use getters before load.");
      }
      return api._q.push(["get", [].slice.call(arguments)]);
    },
    call() {
      api._q.push(["call", [].slice.call(arguments)]);
    },
    init() {
      /* loaded via loadKefuPlugin */
    },
  };
  window.LiveChatWidget = window.LiveChatWidget || api;
  return window.LiveChatWidget;
}

function waitUntilReady(timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = (ok) => {
      if (settled) {
        return;
      }
      settled = true;
      if (ok) {
        resolve(window.LiveChatWidget);
      } else {
        reject(new Error("LiveChat ready timeout"));
      }
    };

    try {
      if (window.LiveChatWidget && window.LiveChatWidget._h) {
        done(true);
        return;
      }
      window.LiveChatWidget.on("ready", () => done(true));
    } catch (error) {
      /* ignore */
    }
    setTimeout(() => {
      if (window.LiveChatWidget && typeof window.LiveChatWidget.call === "function") {
        done(true);
      } else {
        done(false);
      }
    }, timeoutMs);
  });
}

export function loadKefuPlugin() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("no window"));
  }
  ensureLiveChatBootstrap();
  injectLiveChatPositionStyle();

  if (pluginLoadPromise) {
    return pluginLoadPromise;
  }

  if (window.LiveChatWidget && window.LiveChatWidget._h) {
    hookLiveChatVisibility();
    pluginLoadPromise = Promise.resolve(window.LiveChatWidget);
    return pluginLoadPromise;
  }

  pluginLoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("livechat-tracking-script");
    const afterReady = () => {
      hookLiveChatVisibility();
      // 仅用 CSS 藏悬浮球；不要在这里 hide()，否则随后 maximize 会被拖死
      resolve(window.LiveChatWidget);
    };

    if (!existing) {
      const script = document.createElement("script");
      script.id = "livechat-tracking-script";
      script.async = true;
      script.type = "text/javascript";
      script.src = LIVECHAT_SCRIPT_SRC;
      script.onerror = () => {
        pluginLoadPromise = null;
        reject(new Error("LiveChat script load failed"));
      };
      document.head.appendChild(script);
    }

    waitUntilReady()
      .then(afterReady)
      .catch((error) => {
        pluginLoadPromise = null;
        reject(error);
      });
  });

  return pluginLoadPromise;
}

function applyCustomerIdentity(userId, visitorName) {
  const widget = window.LiveChatWidget;
  if (!widget || typeof widget.call !== "function") {
    return;
  }
  const name = String(visitorName || userId).trim() || userId;
  try {
    widget.call("set_customer_name", name);
  } catch (error) {
    /* ignore */
  }
  try {
    widget.call("set_session_variables", {
      客户ID: String(userId),
      customer_id: String(userId),
      member_code: String(userId),
      user_id: String(userId),
    });
  } catch (error) {
    /* ignore */
  }
}

/**
 * 内嵌打开 LiveChat：写入客户ID 后 maximize
 */
export async function openLiveChat(options = {}) {
  const userId = String(options.userId || "").trim();
  const visitorName = String(options.visitorName || userId).trim() || userId;
  if (!userId) {
    return false;
  }

  openingChat = true;
  clearPendingHides();

  try {
    await loadKefuPlugin();
    applyCustomerIdentity(userId, visitorName);

    // eslint-disable-next-line no-console
    console.log("[kefu] LiveChat customerId=", userId, "name=", visitorName);

    // 先从 hidden 拉起，再 maximize；短延迟再 maximize 一次防竞态
    try {
      window.LiveChatWidget.call("maximize");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[kefu] LiveChat maximize failed", error);
      openingChat = false;
      return false;
    }

    setTimeout(() => {
      try {
        window.LiveChatWidget.call("maximize");
      } catch (error) {
        /* ignore */
      }
      // 给展开一点时间，再允许后续 hide
      setTimeout(() => {
        openingChat = false;
      }, 1500);
    }, 300);

    return true;
  } catch (error) {
    openingChat = false;
    throw error;
  }
}

/** 兼容旧调用名 */
export function openSaleSmartlyChat(options) {
  return openLiveChat(options);
}

/** @deprecated */
export function buildKefuChatUrl() {
  return `https://www.livechat.com/chat-with/${LIVECHAT_LICENSE}/`;
}

/** @deprecated */
export function openKefuChatUrl(url, options = {}) {
  if (options && options.newTab && url) {
    window.open(url, "_blank");
    return true;
  }
  return openLiveChat({
    userId: options && options.userId,
    visitorName: options && options.visitorName,
  });
}
